import React from "react";
import SEOHead from "../components/SEOHead";
import PageTransition from "../components/PageTransition";
import AnimatedSection from "../components/AnimatedSection";
import GradientHero from '../components/GradientHero';
import { motion } from "framer-motion";
import {
  Shield, FileSearch, ClipboardList, AlertTriangle, Database,
  Link as LinkIcon, Phone, Mail, Building2, Cookie, Settings, CheckCircle2
} from "lucide-react";

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5 }
  })
};

const lastUpdate = "26/09/2025"; // Europe/Paris

const Privacy: React.FC = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Politique de confidentialité",
    "url": "https://neoqualis.com/confidentialite",
    "description": "Politique de confidentialité de Neoqualis (site vitrine statique, RGPD, cookies, droits utilisateurs)."
  };

  return (
    <PageTransition>
      <SEOHead
        title="Politique de confidentialité | Neoqualis"
        description="RGPD : données, finalités, cookies, droits, contact DPO. Site vitrine statique hébergé chez Hostinger."
        canonical="https://neoqualis.com/confidentialite"
        jsonLd={jsonLd}
      />

      <div className="pt-16">
        {/* Hero */}
        <GradientHero
            title="Politique de confidentialité"
            subtitle={`Dernière mise à jour : ${lastUpdate}`}
            />

        {/* Intro */}
        <section className="section bg-white">
          <div className="container max-w-4xl">
            <AnimatedSection>
              <p className="text-gray-700">
                La présente Politique décrit comment <strong>Neoqualis</strong> (« nous ») traite vos données personnelles
                lorsque vous visitez le site <strong>https://neoqualis.com</strong> (le « Site »), actuellement opéré
                comme <strong>site vitrine statique</strong>.
              </p>
            </AnimatedSection>
          </div>
        </section>

        {/* Responsable & Contact */}
        <section className="section bg-gray-50">
          <div className="container max-w-5xl">
            <AnimatedSection className="text-center mb-10">
              <h2 className="heading">Responsable de traitement & contacts</h2>
              <p className="subheading max-w-2xl mx-auto">
                Vos interlocuteurs pour toute question liée à la protection des données.
              </p>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                custom={0}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
              >
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Building2 className="text-primary w-7 h-7" />
                    </div>
                    <h3 className="text-xl font-semibold ml-4 text-gray-900">Responsable</h3>
                  </div>
                  <div className="text-gray-700 space-y-2">
                    <p><strong>Neoqualis</strong></p>
                    <p>2-4 Rue Vincent Van Gogh,<br/>93360 Neuilly-Plaisance — France</p>
                    <p className="flex items-center"><Phone className="w-4 h-4 mr-2" /> +33 1 86 04 61 82</p>
                    <p className="flex items-center"><Mail className="w-4 h-4 mr-2" /> contact@neoqualis.com</p>
                    <p>Hébergeur : <strong>Hostinger</strong> (UE)</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                custom={1}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
              >
                <div className="card h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-full bg-secondary/10">
                      <Shield className="text-secondary w-7 h-7" />
                    </div>
                    <h3 className="text-xl font-semibold ml-4 text-gray-900">DPO (Délégué à la protection des données)</h3>
                  </div>
                  <div className="text-gray-700 space-y-2">
                    <p><strong>Aghilas DAHMANI</strong></p>
                    <p className="flex items-center"><Mail className="w-4 h-4 mr-2" /> aghilas.dahmani@neoqualis.com</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Données collectées */}
        <section className="section bg-white">
          <div className="container max-w-5xl">
            <AnimatedSection className="text-center mb-10">
              <h2 className="heading">Quelles données collectons-nous ?</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-3">
                    <div className="p-3 rounded-full bg-primary/10">
                      <FileSearch className="text-primary w-7 h-7" />
                    </div>
                    <h3 className="text-lg font-semibold ml-4 text-gray-900">Fonctionnement du Site</h3>
                  </div>
                  <p className="text-gray-700">
                    Aucune donnée personnelle n’est requise pour consulter le Site. Des <strong>journaux techniques</strong>
                    (IP abrégée/anonymisée, user-agent, pages, date/heure) peuvent être traités pour la sécurité et la délivrance
                    des pages.
                  </p>
                </div>
              </motion.div>

              <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-3">
                    <div className="p-3 rounded-full bg-secondary/10">
                      <ClipboardList className="text-secondary w-7 h-7" />
                    </div>
                    <h3 className="text-lg font-semibold ml-4 text-gray-900">Formulaire & contact direct</h3>
                  </div>
                  <p className="text-gray-700">
                    Si vous nous contactez (formulaire, email ou téléphone), nous traitons les informations fournies
                    (identité, coordonnées, message) afin de répondre à votre demande.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Finalités & bases légales */}
        <section className="section bg-gray-50">
          <div className="container max-w-5xl">
            <AnimatedSection className="text-center mb-8">
              <h2 className="heading">Finalités & bases légales</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: "Répondre à vos demandes",
                  text: "Traitement de vos messages et devis.",
                  base: "Mesures précontractuelles (art. 6-1-b RGPD)."
                },
                {
                  title: "Exploiter & sécuriser le Site",
                  text: "Logs, anti-abus, disponibilité.",
                  base: "Intérêt légitime (art. 6-1-f)."
                },
                {
                  title: "Conformité",
                  text: "Gestion des droits, obligations légales.",
                  base: "Obligation légale (art. 6-1-c)."
                }
              ].map((b, i) => (
                <motion.div key={b.title} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                  <div className="card h-full">
                    <div className="flex items-center mb-3">
                      <div className="p-3 rounded-full bg-primary/10">
                        <Settings className="text-primary w-7 h-7" />
                      </div>
                      <h3 className="text-lg font-semibold ml-4 text-gray-900">{b.title}</h3>
                    </div>
                    <p className="text-gray-700 mb-2">{b.text}</p>
                    <p className="text-sm text-gray-600"><em>Base légale :</em> {b.base}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Cookies */}
        <section className="section bg-white">
          <div className="container max-w-5xl">
            <AnimatedSection className="text-center mb-8">
              <h2 className="heading">Cookies & traceurs</h2>
              <p className="subheading max-w-2xl mx-auto">
                Par défaut, nous n’utilisons aucun cookie non essentiel.
              </p>
            </AnimatedSection>

            <div className="card max-w-4xl mx-auto">
              <div className="flex items-center mb-3">
                <div className="p-3 rounded-full bg-secondary/10">
                  <Cookie className="text-secondary w-7 h-7" />
                </div>
                <h3 className="text-lg font-semibold ml-4 text-gray-900">Mesure d’audience respectueuse (option)</h3>
              </div>
              <p className="text-gray-700">
                Nous pouvons activer une mesure d’audience <strong>sans cookies</strong> et <strong>sans suivi individuel</strong>
                (ex. Cloudflare Web Analytics). Dans ce cas, <strong>aucun bandeau de consentement</strong> n’est requis.
                Si nous utilisons un outil soumis au consentement, nous l’indiquerons clairement et recueillerons
                votre choix avant tout dépôt.
              </p>
            </div>
          </div>
        </section>

        {/* Durées, destinataires, transferts */}
        <section className="section bg-gray-50">
          <div className="container max-w-5xl">
            <AnimatedSection className="text-center mb-8">
              <h2 className="heading">Conservation, destinataires, transferts</h2>
            </AnimatedSection>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-3">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Database className="text-primary w-7 h-7" />
                    </div>
                    <h3 className="text-lg font-semibold ml-4 text-gray-900">Durées</h3>
                  </div>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li><CheckCircle2 className="w-4 h-4 text-secondary inline mr-2" />Contacts : durée du traitement + jusqu’à <strong>3 ans</strong> après dernier échange.</li>
                    <li><CheckCircle2 className="w-4 h-4 text-secondary inline mr-2" />Logs techniques : <strong>6–12 mois</strong> (sécurité).</li>
                    <li><CheckCircle2 className="w-4 h-4 text-secondary inline mr-2" />Mesure d’audience (si activée) : selon l’outil configuré.</li>
                  </ul>
                </div>
              </motion.div>

              <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-3">
                    <div className="p-3 rounded-full bg-secondary/10">
                      <LinkIcon className="text-secondary w-7 h-7" />
                    </div>
                    <h3 className="text-lg font-semibold ml-4 text-gray-900">Destinataires</h3>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Accès limité aux personnes habilitées de Neoqualis et à nos prestataires (hébergement, sécurité,
                    maintenance, mesure d’audience le cas échéant) agissant <strong>pour notre compte</strong> et selon
                    nos instructions.
                  </p>
                </div>
              </motion.div>

              <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants}>
                <div className="card h-full">
                  <div className="flex items-center mb-3">
                    <div className="p-3 rounded-full bg-primary/10">
                      <AlertTriangle className="text-primary w-7 h-7" />
                    </div>
                    <h3 className="text-lg font-semibold ml-4 text-gray-900">Transferts hors UE</h3>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Nous privilégions des traitements au sein de l’UE/EEE. Si un transfert est nécessaire (ex. prestataire
                    situé hors UE), nous utilisons un mécanisme valide (décision d’adéquation, clauses contractuelles types,
                    mesures complémentaires si besoin).
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Droits RGPD */}
        <section className="section bg-white">
          <div className="container max-w-5xl">
            <AnimatedSection className="text-center mb-8">
              <h2 className="heading">Vos droits</h2>
            </AnimatedSection>

            <div className="card max-w-4xl mx-auto">
              <p className="text-gray-700">
                Dans les conditions prévues par la loi, vous disposez des droits d’<strong>accès</strong>, de
                <strong> rectification</strong>, d’<strong>effacement</strong>, de <strong>limitation</strong>,
                d’<strong>opposition</strong>, de <strong>portabilité</strong> ainsi que du <strong>retrait du
                consentement</strong> lorsque celui-ci est la base du traitement.
                Pour exercer vos droits : <strong>contact@neoqualis.com</strong> ou à l’adresse postale ci-dessus.
                Vous pouvez aussi saisir la <strong>CNIL</strong> (www.cnil.fr).
              </p>
            </div>
          </div>
        </section>

        {/* Modifications */}
        <section className="section bg-gray-50">
          <div className="container max-w-5xl">
            <AnimatedSection className="text-center mb-8">
              <h2 className="heading">Modifications</h2>
            </AnimatedSection>

            <div className="card max-w-4xl mx-auto">
              <p className="text-gray-700">
                Nous pouvons mettre à jour cette Politique pour des raisons légales, techniques ou fonctionnelles.
                La date de dernière mise à jour fait foi. En cas de changement substantiel, une information pourra
                être affichée sur le Site.
              </p>
            </div>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default Privacy;
